import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActive from 'src/components/Badge/BadgeActive';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import buildStandingChargeRate from 'src/util/i18n/tariff/standingCharge';
import logger from 'src/util/logger';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more standing charges. Please try again.',
  },
});

const AdminStandingChargeListFragment = graphql`
  fragment AdminStandingChargeList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminStandingChargeListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  )
  {
    id
    standingCharges(
      first: $count
      after: $cursor
    ) @connection(key: "AdminStandingChargeList_standingCharges") {
      edges {
        cursor
        node {
          uuid
          externalIdentifier
          identifier
          title
          rate
          timeUnit
          tariff {
            uuid
            title
            active { start finish }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={6} rowCount={3} />;
}

/**
 * Admin list of standing charges with load-more pagination. Field errors throw
 * to the route's PlaceholderErrorBoundary; load-more failures show an inline
 * retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactElement} AdminStandingChargeList component.
 */
function AdminStandingChargeList({ viewer: viewerRef = null }) {
  const {
    data: viewer, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminStandingChargeListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);

  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  if (!viewer) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminStandingChargeList: the route query returned no viewer');
  }

  const { standingCharges } = viewer;
  // Page title and meta information
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.page.title',
    defaultMessage: 'Enosi Admin - Standing Charges',
  });

  // Breadcrumb labels
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbStandingCharges = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.breadcrumbs.standing_charges.label',
    defaultMessage: 'Standing Charges',
  });

  // Table headers
  const tableHeaderTitle = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.title.label',
    defaultMessage: 'Title',
  });
  const tableHeaderIdentifier = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.identifier.label',
    defaultMessage: 'Identifier',
  });
  const tableHeaderId = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.id.label',
    defaultMessage: 'ID',
  });
  const tableHeaderExternalIdentifier = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.external_identifier.label',
    defaultMessage: 'External Identifier',
  });
  const tableHeaderExtId = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.external_identifier.short.label',
    defaultMessage: 'Ext ID',
  });
  const tableHeaderRate = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.rate.label',
    defaultMessage: 'Rate',
  });
  const tableHeaderTariff = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.table.header.tariff.label',
    defaultMessage: 'Tariff',
  });

  // Load more button states
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_standing_charge.admin_standing_charge_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more standing charges to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbStandingCharges },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbStandingCharges}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{tableHeaderTitle}</th>
                <th scope="col">{tableHeaderIdentifier}</th>
                <th scope="col">{tableHeaderId}</th>
                <th scope="col">
                  <abbr title={tableHeaderExternalIdentifier}>{tableHeaderExtId}</abbr>
                </th>
                <th scope="col">{tableHeaderRate}</th>
                <th scope="col">{tableHeaderTariff}</th>
              </tr>
            </thead>
            <tbody>
              {
                standingCharges && standingCharges.edges && standingCharges.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, title, identifier, externalIdentifier, rate, timeUnit, tariff,
                  } = node;

                  return (
                    <tr key={`admin-standing-charge-${uuid}`}>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/standing-charges/${uuid}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={uuid} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {buildStandingChargeRate(intl, intlCurrency, timeUnit, rate)}
                      </td>
                      <td>
                        {tariff && (
                          <>
                            <Link to={`/admin/tariffs/${tariff.uuid}`}>
                              {tariff.title}
                            </Link>
                            <br />
                            <BadgeActive
                              start={tariff.active.start}
                              finish={tariff.active.finish}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminStandingChargeList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminStandingChargeList;
